import React, { useMemo, useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Modal, Select, DatePicker, Button, Spin } from "antd";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
// Updated: Paginated Table
import Table from "../../../core/pagination/Newdatatable";
import GRAN_LOGO1 from "../Logo/Granules_logo1.png";
import { getApiBaseUrl, ADD_ACTIVITY, LOGIN } from "../Config/Config";
import dayjs from "dayjs"; // Only needed for AntD RangePicker

const { Option } = Select;
const { RangePicker } = DatePicker;

const FilterHistoryReport = () => {
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [downloadReportLoading, setDownloadReportLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [dataSource, setDataSource] = useState([]); // Paginated data
  const [selectedFilterId, setSelectedFilterId] = useState(0);
  const [selectedFilterName, setSelectedFilterName] = useState("All");

  // dayjs only for RangePicker
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "day"),
    dayjs(),
  ]);

  const [filters, setFilters] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  const user = JSON.parse(sessionStorage.getItem("user")) || {
    UserName: "Unknown User",
    UserID: null,
  };

  const reportConfig = useMemo(() => {
    const now = dayjs();
    return {
      title: "Filter History Report",
      filename: `Filter_History_Report_${now.format("DD-MM-YYYY_HH-mm-ss")}`,
      columns: [
        {
          title: "Date & Time",
          key: "CreatedOn",
          render: (text) => {
            if (!text) return "NA";
            const date = new Date(text);
            if (isNaN(date.getTime())) return "NA";
            const day = String(date.getUTCDate()).padStart(2, "0");
            const month = String(date.getUTCMonth() + 1).padStart(2, "0");
            const year = date.getUTCFullYear();
            const hours = String(date.getUTCHours()).padStart(2, "0");
            const minutes = String(date.getUTCMinutes()).padStart(2, "0");
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          },
        },
        { title: "Action", key: "StageName" },
        { title: "Block", key: "BlockName" },
        { title: "User ID", key: "username" },
        {
          title: "Data",
          key: "Data",
          render: (_, record) => {
            const reason = record?.washingreason || "-";
            const air = record?.AirPressure || "-";
            const ro = record?.RoWaterPressure || "-";
            const temp = record?.DryerTemp || "-";
            const duration = record?.Duration || "-";

            const stage = (record?.StageName || "").toLowerCase();

            switch (stage) {
              case "wash-in":
                return (
                  <>
                    <div>Washing Reason: {reason}</div>
                    <div>Compressed Air Pressure: {air}</div>
                    <div>RO Water Pressure: {ro}</div>
                  </>
                );
              case "dry-in":
                return (
                  <>
                    <div>Duration: {duration} Min</div>
                    <div>Temperature: {temp}</div>
                  </>
                );
              case "wash-out":
              case "dry-out":
              case "storage-in":
              case "storage-out":
                return <div>NA</div>;
              default:
                return <div>-</div>;
            }
          },
        },
        { title: "Remarks", key: "Remarks" },
      ],
    };
  }, []);

  const formatDateForAPI = (dayjsObj) => {
    return dayjsObj.format("YYYY-MM-DD HH:mm:ss");
  };

  // Paginated fetch
  const fetchFilterHistoryData = async (page = 1, limit = 100) => {
    setLoading(true);
    try {
      const startDate = formatDateForAPI(dateRange[0]);
      const endDate = formatDateForAPI(dateRange[1]);
      const filterId = selectedFilterId || 0;

      const url = `${getApiBaseUrl()}/filterph2/getFilterHistoryReportpg?Filterid=${filterId}&startdate=${encodeURIComponent(
        startDate
      )}&enddate=${encodeURIComponent(endDate)}&pageNumber=${page}&recordslimit=${limit}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.Status && Array.isArray(data.ResultData)) {
        const list = data.ResultData || [];
        setDataSource(list.map((item, idx) => ({ ...item, key: idx })));
        if (list.length > 0 && list[0].TotalCount !== undefined) {
          setTotalRecords(list[0].TotalCount);
        } else {
          setTotalRecords(0);
        }
      } else {
        setDataSource([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching filter history:", error);
      setDataSource([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Full data fetch for PDF
  const fetchAllDataForPDF = async () => {
    if (totalRecords === 0) return [];

    try {
      const startDate = formatDateForAPI(dateRange[0]);
      const endDate = formatDateForAPI(dateRange[1]);
      const filterId = selectedFilterId || 0;

      const url = `${getApiBaseUrl()}/filterph2/getFilterHistoryReportpg?Filterid=${filterId}&startdate=${encodeURIComponent(
        startDate
      )}&enddate=${encodeURIComponent(endDate)}&pageNumber=1&recordslimit=${totalRecords}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.Status && Array.isArray(data.ResultData)) {
        return data.ResultData || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching full history for PDF:", error);
      return [];
    }
  };

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/getFiltersListDropdown`)
      .then((res) => res.json())
      .then((data) => setFilters(data?.ResultData || []))
      .catch(() => setFilters([]));
  }, []);

  // On filter/date change
  useEffect(() => {
    setCurrentPage(1);
    fetchFilterHistoryData(1, pageSize);
  }, [selectedFilterId, dateRange]);

  // On pagination change
  useEffect(() => {
    fetchFilterHistoryData(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const tableColumns = useMemo(
    () =>
      reportConfig.columns.map((col) => ({
        title: col.title,
        dataIndex: col.key,
        key: col.key,
        render: col.render || ((text) => text || "NA"),
      })),
    [reportConfig]
  );

  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );

  const handleExportPDF = async () => {
    setDownloadReportLoading(true);

    const fullData = await fetchAllDataForPDF();

    const doc = new jsPDF();
    const logoWidth = 15;
    const logoHeight = 15;
    const pageWidth = doc.internal.pageSize.getWidth();

    const generateHeader = () => {
      doc.addImage(GRAN_LOGO1, "PNG", 10, 10, logoWidth, logoHeight);
      doc.setFontSize(11);
      doc.text("Granules India Limited", 30, 14);
      doc.text("Gagillapur", 30, 20);
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(`Title: ${reportConfig.title}`, 10, 35);
      doc.setFont(undefined, "normal");
      return 42;
    };

    const startY = generateHeader();

    const formatDataCell = (record) => {
      const reason = record?.washingreason || "NA";
      const air = record?.AirPressure || "NA";
      const ro = record?.RoWaterPressure || "NA";
      const temp = record?.DryerTemp || "NA";
      const duration = record?.Duration || "NA";
      const stage = (record?.StageName || "").toLowerCase();

      switch (stage) {
        case "wash-in":
          return `Washing Reason: ${reason}\nCompressed Air Pressure: ${air}\nRO Water Pressure: ${ro}`;
        case "dry-in":
          return `Duration: ${duration} Min\nTemperature: ${temp}`;
        case "wash-out":
        case "dry-out":
        case "storage-in":
        case "storage-out":
          return "NA";
        default:
          return "NA";
      }
    };

    autoTable(doc, {
      startY,
      head: [reportConfig.columns.map((col) => col.title)],
      body:
        fullData.length > 0
          ? fullData.map((item) => [
              item.CreatedOn
                ? (() => {
                    const d = new Date(item.CreatedOn);
                    return `${String(d.getUTCDate()).padStart(2, "0")}/${String(
                      d.getUTCMonth() + 1
                    ).padStart(2, "0")}/${d.getUTCFullYear()} ${String(
                      d.getUTCHours()
                    ).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
                  })()
                : "NA",
              item.StageName || "NA",
              item.BlockName || "NA",
              item.username || "NA",
              formatDataCell(item),
              item.Remarks || "NA",
            ])
          : [[{ content: "No Data Available", colSpan: 6, styles: { halign: "center" } }]],
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
      margin: { top: startY, bottom: 35 },
      didDrawPage: generateHeader,
    });

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 40, doc.internal.pageSize.height - 10);
      if (i === totalPages) {
        const footerY = doc.internal.pageSize.height - 20;
        doc.text("Remarks:", 10, footerY - 10);
        doc.text(`Printed by: ${user.UserName}`, 10, footerY);
        doc.text(
          `Date Range: ${dateRange[0].format("DD-MM-YYYY HH:mm")} - ${dateRange[1].format("DD-MM-YYYY HH:mm")}`,
          10,
          footerY + 6
        );
        doc.text(`Printed on: ${dayjs().format("DD-MM-YYYY HH:mm:ss")}`, 10, footerY + 12);
        doc.text(`Filter: ${selectedFilterName}`, 10, footerY + 18);
      }
    }

    const pdfBlob = doc.output("blob");
    const formData = new FormData();
    formData.append("file", pdfBlob, `${reportConfig.filename}.pdf`);

    fetch(`${getApiBaseUrl()}/api/uploadReportCopy`, { method: "POST", body: formData }).catch((err) =>
      console.error("Upload failed:", err)
    );

    doc.save(`${reportConfig.filename}.pdf`);
    setDownloadReportLoading(false);
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError("Please enter your password");
      return;
    }

    setAuthLoading(true);
    setPasswordError("");
    const username = user?.UserName || user?.Username;
    let loginSuccessful = false;

    try {
      const loginRes = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const loginData = await loginRes.json();
      loginSuccessful = loginData?.Message === "Login successful";
    } catch (error) {
      setPasswordError("Something went wrong. Please try again.");
    }

    try {
      const activityPayload = {
        ActivityType: 1,
        PerformedBy: user?.UserID,
        PerformedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        Notes: loginSuccessful
          ? `${reportConfig.title} downloaded by ${username}`
          : `Failed Reauthentication for ${reportConfig.title} by ${username}`,
        Location: "Reports",
        IsSucces: loginSuccessful ? 1 : 0,
      };
      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(activityPayload),
      });
    } catch (err) {
      console.error("Activity log error:", err);
    } finally {
      setAuthLoading(false);
      if (loginSuccessful) {
        setVisible(false);
        setPassword("");
        handleExportPDF();
      }
    }
  };

  const handleFilterIdChange = (value) => {
    setSelectedFilterId(value);
    const selected = filters.find((f) => f.ID === value);
    setSelectedFilterName(selected ? selected.Lable : "All");
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    } else {
      setDateRange([dayjs().subtract(7, "day"), dayjs()]);
    }
  };

  return (
    <>
      {downloadReportLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 1050, backdropFilter: "blur(4px)", backgroundColor: "rgba(255,255,255,0.2)" }}
        >
          <Spin size="large" tip="Generating PDF with all records..." />
        </div>
      )}

      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/assetmgt/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item" aria-current="page">
                      {reportConfig.title}
                    </h4>
                  </ol>
                </nav>
                <h6>Manage your Filter History reports</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip}>
                  <Link
                    to="#"
                    onClick={() => {
                      setVisible(true);
                      setPassword("");
                      setPasswordError("");
                    }}
                  >
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap gap-3">
                <div style={{ width: 320 }}>
                  <Select
                    style={{ width: "100%" }}
                    value={selectedFilterId}
                    onChange={handleFilterIdChange}
                    placeholder="Select Filter ID"
                    showSearch
                    optionFilterProp="children"
                  >
                    <Option value={0}>All</Option>
                    {filters?.map((filter) => (
                      <Option key={filter.ID} value={filter.ID}>
                        {filter.Lable}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <RangePicker
                    showTime={{ format: "HH:mm" }}
                    format="DD-MM-YYYY HH:mm"
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    style={{ width: 350 }}
                  />
                  <Button
                    type="primary"
                    onClick={() => {
                      setCurrentPage(1);
                      fetchFilterHistoryData(1, pageSize);
                    }}
                    loading={loading}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center my-4">
                    <Spin size="large" />
                    <p>Loading data...</p>
                  </div>
                ) : dataSource.length > 0 ? (
                  <Table
                    columns={tableColumns}
                    dataSource={dataSource}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={(page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    }}
                  />
                ) : (
                  <p className="text-center my-4">No data available</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal
          title="Confirm Password"
          open={visible}
          onCancel={() => {
            setVisible(false);
            setPassword("");
            setPasswordError("");
          }}
          onOk={handlePasswordSubmit}
          confirmLoading={authLoading}
          okText="Verify & Download"
          width={350}
        >
          <div className="mb-3">
            <div className="pass-group">
              <input
                type={showPassword ? "text" : "password"}
                className={`form-control ${passwordError ? "is-invalid" : ""}`}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError("");
                }}
                autoComplete="new-password"
                onCopy={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
              />
              <span
                className={`ti toggle-password ${showPassword ? "ti-eye" : "ti-eye-off"}`}
                onClick={() => setShowPassword((prev) => !prev)}
              ></span>
            </div>
            {passwordError && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {passwordError}
              </div>
            )}
          </div>
        </Modal>
      </div>
    </>
  );
};

export default FilterHistoryReport;